/**
 * EditorHeader Component for UI-Model-1
 *
 * Top bar with back button, model name, edit button, and preview button.
 * Handles navigation back to list view and cache clearing.
 */

import React, { Dispatch, SetStateAction } from 'react';
import COMPATIO_LOGO from 'assests/img/compatio_logo_without_name.png';
import { BackIcon, EditIcon } from 'assests/GmsIcons';
import { UIModelAPI } from '../../types/api.types';
import { useVerticalNav } from '../../contexts/VerticalNavContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Button from 'Components/DesignSystem/Button/Button';
import { FOCUS_RING } from 'Components/DesignSystem/focusRing';
import { isFreestyle } from '../../selectors';
import FreestyleBadge from '../Common/FreestyleBadge';

interface EditorHeaderProps {
  id?: string;
  uiModel: UIModelAPI | null;
  onBackClick: () => void;
  onEditClick: () => void;
  onPreviewClick: () => void;
  hasEditPermission?: boolean;
}

export const EditorHeader = ({
  id,
  uiModel,
  onBackClick,
  onEditClick,
  onPreviewClick,
  hasEditPermission = true,
}: EditorHeaderProps) => {
  const baseId = id || 'editor-header';
  const { setActiveTab } = useVerticalNav();
  const { dispatch } = useUIModelState();

  const handleBackClick = () => {
    // Reset vertical nav to layout tab
    setActiveTab('layout');

    // Clear UI Model state
    dispatch({ type: 'RESET_STATE' });

    // Call parent's back handler (navigates to list)
    onBackClick();
  };

  const handleEditClick = () => {
    if (!hasEditPermission) {
      toast.error("You don't have permission to update this model");
      return;
    }
    onEditClick();
  };

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className="tw-p-1 tw-border-b tw-bg-surface-sunken tw-border-edge"
    >
      <div
        id={`${baseId}-content`}
        className="tw-flex tw-justify-between tw-items-center"
      >
        {/* Left section: Back, Logo, Title, Edit */}
        <div
          id={`${baseId}-left-section`}
          className="tw-flex tw-items-center tw-space-x-2"
        >
          {/* A real <button> (was a div role="button" with no tabIndex — never
              keyboard-reachable, same fix as MainPageHeader's back control).
              BackIcon hardcodes fill="#172B4D", so fill-current re-inks it. */}
          <button aria-label="Back" type="button"
            id={`${baseId}-back-btn`}
            data-testid={`${baseId}-back-btn`}
            onClick={handleBackClick}
            className={`tw-cursor-pointer tw-text-ink-secondary hover:tw-text-ink icon-fill-current ${FOCUS_RING}`}
          >
            <BackIcon />
          </button>
          <img
            id={`${baseId}-icon`}
            src={uiModel?.launchIcon || COMPATIO_LOGO}
            alt="Launcher Icon"
            loading="lazy"
            className="tw-w-8 tw-h-8 tw-rounded tw-border tw-border-edge-strong"
          />
          <h2
            id={`${baseId}-title`}
            className="tw-text-ink tw-text-base tw-font-semibold"
          >
            {uiModel?.name || 'Untitled Model'}
          </h2>
          {/* EditIcon already draws with stroke="currentColor" — naming the ink
              is all it needs. The permission gate stays a toast, not disabled. */}
          <button aria-label="Edit"
            id={`${baseId}-edit-btn`}
            data-testid={`${baseId}-edit-btn`}
            onClick={handleEditClick}
            className={`tw-text-ink-secondary hover:tw-text-ink ${FOCUS_RING}`}
          >
            <EditIcon />
          </button>
          {isFreestyle(uiModel) && <FreestyleBadge id={`${baseId}-freestyle-badge`} />}
        </div>

        {/* Right section: Preview button — DS Button replaces the global
            `samll-outline-btn` (main.css hardcodes #091caa/#707275 in it; the
            other 8 call sites migrate with their own modules). */}
        <Button
          variant="secondary"
          size="sm"
          id={`${baseId}-preview-btn`}
          data-testid={`${baseId}-preview-btn`}
          onClick={onPreviewClick}
        >
          Preview
        </Button>
      </div>
    </div>
  );
};

export default EditorHeader;
